/**
 * TabletLayout — iPad and Android tablets.
 *
 * Layout: persistent sidebar + content pane.
 * iPad landscape (≥1024px): 240px sidebar
 * iPad portrait / Android tablet (<1024px): 200px sidebar
 *
 * Bug fixes vs previous version:
 *   1. TabletSidebar NO LONGER calls useSidebarWidth() internally — it was
 *      shadowing the prop and running a redundant hook instance.
 *   2. Top bar paddingLeft now uses the dynamic sidebarWidth state variable,
 *      not the SIDEBAR_W constant. Eliminated the 20px misalignment on portrait iPad.
 *
 * Performance contract:
 *   ✗ NO backdrop-filter/blur
 *   ✗ NO will-change on scroll containers
 *   ✓ contain: layout style paint on sidebar and top bar
 *   ✓ translateZ(0) on sidebar and top bar (own GPU layers)
 *   ✓ overscroll-behavior: contain on all scroll panes
 *   ✓ touch-action: manipulation on all interactive elements
 *   ✓ min touch targets 44×44px throughout
 *   ✓ Orientation-aware sidebar width via useSidebarWidth()
 */
import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, MessageSquare, Bell, User,
  Users, BarChart2, Settings, LogOut, Shield, X, WifiOff, Layers, Receipt, Plus, DollarSign, ChevronDown, Trophy,
  ChevronLeft, Radio,
} from 'lucide-react';
import { PRESS_BOX, pressBoxVisible, pressBoxNavItems } from '../constants/pressBox.js';
import { TEAM_SETTINGS_SUBSECTIONS } from './teamSettingsSubNav.js';
// v2.5.124 — Phase 11 League Module.
import { useLeagueModeNav } from '../hooks/useLeagueModeNav.js';
import { useCurrentSeason } from '../hooks/useCurrentSeason.js';
import { pathForTeamSwitch } from './teamSwitchNav.js';
import { useAuth } from '../context/AuthContext.jsx';
import { usePWAContext } from '../context/PWAContext.jsx';
import { chatSocket } from '../services/socket.js';
import api from '../services/api.js';
import { AppBanner } from '../components/shared/AppBanner.jsx';
import { PWABanners } from '../components/shared/PWABanners.jsx';
import VersionTag from '../components/shared/VersionTag.jsx';
import OnboardingFlow from '../components/OnboardingFlow.jsx';
import { iconForNotifType } from '../utils/notifIcons.js';
import { pickActiveTeam, saveLastTeamId } from '../utils/lastTeam.js';

// ─── Solid surface tokens ──────────────────────────────────────────
// S uses CSS custom properties so all three themes work automatically
const S = {
  bg:      'var(--bg)',
  sidebar: 'var(--bg-2)',
  surface: 'var(--surface)',
  topBar:  'var(--topbar-bg)',
  border:  'var(--border)',
  text:    'var(--text)',
  text2:   'var(--text-2)',
};

const T = {
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  outline: 'none',
};

// ── Orientation-aware sidebar width ─────────────────────────────────
// Called ONLY in TabletLayout — result passed as a prop to TabletSidebar.
// This is the single source of truth for sidebar width.
function useSidebarWidth() {
  const [w, setW] = useState(window.innerWidth >= 1024 ? 240 : 200);
  useEffect(() => {
    const update = () => setW(window.innerWidth >= 1024 ? 240 : 200);
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  return w;
}

// ─── Persistent sidebar ───────────────────────────────────────────
// sidebarWidth is passed as a prop — this component does NOT call useSidebarWidth() itself.
// (Previous version shadowed the prop with a local hook call — now fixed.)
function TabletSidebar({ teams, activeTeam, setActiveTeam, user, onLogout, unreadCount, sidebarWidth, currentSeasonId, currentSeasonName }) {
  const teamColor = activeTeam?.settings?.team_color || 'var(--ice)';
  const navigate  = useNavigate();
  const location  = useLocation();

  // Apr 2026 — split nav by role.
  // Players see only Dashboard, Schedule, Chat, Team. Players page,
  // Seasons, and Stats are coach/manager-only.
  const isCoachish = activeTeam && ['coach','manager'].includes(activeTeam.member_role);

  // 2026-10-18 — a Press Box team gets its own slim menu (games, roster,
  // site & settings) instead of the full team nav.
  const navItems = activeTeam?.team_type === 'press_box'
    ? pressBoxNavItems(activeTeam, { Radio, Users, Settings })
    : activeTeam ? [
    { to: '/dashboard',                     icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: `/team/${activeTeam.id}/games`,   icon: Calendar,        label: 'Schedule' },
    // Apr 30 2026 — Scoreboards. Visible to all roster members,
    // hidden when team setting scoreboard_enabled === false.
    ...(activeTeam.settings?.scoreboard_enabled !== false ? [
      { to: `/team/${activeTeam.id}/scoreboards`, icon: Trophy, label: 'Scoreboards' },
    ] : []),
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/players`, icon: Users,           label: 'Players' },
    ] : []),
    { to: `/team/${activeTeam.id}/chat`,    icon: MessageSquare,   label: 'Chat',  badge: unreadCount },
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/seasons`, icon: Layers,          label: 'Seasons' },
      { to: `/team/${activeTeam.id}/stats`,   icon: BarChart2,       label: 'Stats' },
      // Apr 2026 (Tom_Notes E2) — Finance tab.
      { to: `/team/${activeTeam.id}/finance`, icon: DollarSign,      label: 'Finance' },
      // Budget is OFF by default — only shown when a super admin has
      // enabled it for this team (teams.settings.budget_enabled).
      ...(activeTeam.settings?.budget_enabled === true ? [
        { to: `/team/${activeTeam.id}/budget`,  icon: DollarSign,      label: 'Budget' },
      ] : []),
      // Apr 2026 mobile-parity — Notification Log added to match
      // Desktop/Mobile/Capacitor sidebar inventories. Coach-only.
      { to: `/team/${activeTeam.id}/notification-log`, icon: Bell, label: 'Notification Log' },
    ] : [
      // May 4 2026 (v2.5.70) — player-scoped Stats. See DesktopLayout
      // for full rationale.
      { to: `/team/${activeTeam.id}/my-stats`, icon: BarChart2, label: 'Stats' },
    ]),
    // May 4 2026 (v2.5.70) — Team Settings entry hidden entirely for
    // non-coach players. See MobileLayout for rationale.
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/settings`, icon: Settings,
        label: 'Team Settings',
        // Apr 2026 — expandable sub-nav (coach/manager only).
        children: TEAM_SETTINGS_SUBSECTIONS.map(s => ({
          to: `/team/${activeTeam.id}/settings/${s.id}`,
          label: s.label,
          end: true,
        })),
      },
    ] : []),
    // v2.5.118 — Leagues nav is gated by the per-user 'leagues'
    // feature flag (see MobileLayout for rationale).
  ] : [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
  ];

  if (user?.featureFlags?.leagues) {
    navItems.push({ to: '/leagues', icon: Trophy, label: 'Leagues', accent: 'gold' });
  }
  // 2026-10-17 — Press Box (single-team stats), gated like Leagues: the
  // pressBox flag, or the super admin. The server re-checks every call.
  if (pressBoxVisible(user) && activeTeam?.team_type !== 'press_box') {
    navItems.push({ to: '/press-box', icon: Radio, label: PRESS_BOX.NAME, accent: 'gold' });
  }

  // v2.5.124 — see DesktopLayout for rationale.
  const { inLeagueMode, leagueNavItems, league } = useLeagueModeNav();
  const effectiveNavItems = inLeagueMode && leagueNavItems ? leagueNavItems : navItems;

  return (
    <aside style={{
      width: sidebarWidth,
      flexShrink: 0,
      height: '100%',
      background: `linear-gradient(180deg, ${S.sidebar} 0%, ${S.bg} 100%)`,
      borderRight: `1px solid ${S.border}`,
      display: 'flex', flexDirection: 'column',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: 'touch',
      contain: 'layout style paint',
      transform: 'translateZ(0)',
      // iPad landscape: left safe area (when home button is on left)
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    }}>
      {/* Team color accent bar */}
      <div style={{
        height: 3, flexShrink: 0,
        background: `linear-gradient(90deg, ${teamColor} 0%, transparent 70%)`,
        // No CSS transition — avoids repaints on team switch
      }} />

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 16px 14px', flexShrink: 0 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: `${teamColor}18`, border: `1px solid ${teamColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏟</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 19, letterSpacing: '0.08em', textTransform: 'uppercase', color: S.text, lineHeight: 1 }}>Game Day</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: teamColor, marginTop: 2 }}>TEAM MANAGER</div>
        </div>
      </div>

      {/* League-mode header — v2.5.124. */}
      {inLeagueMode && (
        <div style={{ padding: '0 10px 8px', flexShrink: 0 }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: S.text2, padding: '0 8px', marginBottom: 6 }}>LEAGUE MODE</p>
          <div style={{
            padding: '9px 10px', borderRadius: 8, marginBottom: 3,
            background: 'rgba(178, 132, 255, 0.10)',
            border: '1px solid rgba(178, 132, 255, 0.30)',
            display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
          }}>
            <Trophy size={13} style={{ color: '#b284ff', flexShrink: 0 }} />
            <span style={{
              fontSize: 13, fontWeight: 700, color: S.text,
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              flex: 1, minWidth: 0,
            }}>{league?.name || 'Loading…'}</span>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              ...T, width: '100%', textAlign: 'left',
              padding: '9px 10px', borderRadius: 8, marginTop: 3, minHeight: 40,
              background: 'transparent', border: `1px dashed ${S.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
              color: S.text2, fontFamily: 'var(--font-body)', fontSize: 13,
            }}>
            <ChevronLeft size={14} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, fontWeight: 600 }}>Back to teams</span>
          </button>
        </div>
      )}

      {/* Team picker — hidden in league mode (v2.5.124). */}
      {!inLeagueMode && teams.length > 0 && (
        <div style={{ padding: '0 10px 8px', flexShrink: 0 }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: S.text2, padding: '0 8px', marginBottom: 6 }}>TEAMS</p>
          {teams.map(t => {
            const c = t.settings?.team_color || 'var(--ice)';
            const isA = activeTeam?.id === t.id;
            return (
              <button key={t.id} onClick={() => {
                // Apr 30 2026 — see DesktopLayout for rationale.
                setActiveTeam(t);
                if (activeTeam?.id !== t.id) {
                  const target = pathForTeamSwitch(location.pathname, activeTeam?.id, t.id, t);
                  if (target) navigate(target);
                }
              }} style={{ ...T, width: '100%', textAlign: 'left', padding: '9px 10px', borderRadius: 8, marginBottom: 2, background: isA ? `${c}12` : 'transparent', border: `1px solid ${isA ? `${c}28` : 'transparent'}`, display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, contain: 'layout style' }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: c, opacity: isA ? 1 : 0.3, flexShrink: 0 }} />
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: isA ? 600 : 400, color: isA ? c : S.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{t.name}</span>
              </button>
            );
          })}
          {/* Apr 2026 — + New Team so any player can create their own team. */}
          <button
            onClick={() => navigate('/dashboard?new=1')}
            style={{
              ...T, width: '100%', textAlign: 'left',
              padding: '9px 10px', borderRadius: 8, marginTop: 3, minHeight: 40,
              background: 'transparent',
              border: `1px dashed ${S.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
              color: S.text2, fontFamily: 'var(--font-body)', fontSize: 13,
            }}>
            <Plus size={14} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, fontWeight: 600 }}>New Team</span>
          </button>
        </div>
      )}

      <div style={{ height: 1, background: S.border, margin: '0 14px 8px', flexShrink: 0 }} />

      {/* Nav */}
      <nav style={{ flex: 1, padding: '2px 10px', overflow: 'hidden' }}>
        {activeTeam && (
            <button onClick={() => { navigate(`/team/${activeTeam.id}/seasons`); }}
              title="The season every page is currently showing. Tap to switch."
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', margin: '0 0 8px', padding: '9px 12px', borderRadius: 10,
                background: currentSeasonId ? 'var(--ice-a8, rgba(0,212,255,0.08))' : 'var(--surface-2)',
                border: `1px solid ${currentSeasonId ? 'var(--ice-a30, rgba(0,212,255,0.30))' : 'var(--border)'}`, cursor: 'pointer', textAlign: 'left' }}>
              <Layers size={14} style={{ color: currentSeasonId ? 'var(--ice)' : 'var(--text-3)', flexShrink: 0 }} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', lineHeight: 1.2 }}>Season</span>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: currentSeasonId ? 'var(--ice)' : 'var(--text-2)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {currentSeasonId ? (currentSeasonName || 'Selected season') : 'All seasons'}
                </span>
              </span>
            </button>
          )}
          {effectiveNavItems.map(item => {
          // Apr 2026 — derive expansion from URL. When the user is on
          // any /settings/* route, the children of "Team Settings"
          // render below; navigating away collapses them. Hides
          // chevron + children for items without children (the
          // common case).
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const expanded    = hasChildren && location.pathname.startsWith(item.to);
          // v2.5.126 — gold accent for the Leagues nav item.
          // Resolve once so the styling stays readable.
          const itemAccentColor = item.accent === 'gold' ? 'var(--gold)' : teamColor;
          const itemAccentBg    = item.accent === 'gold' ? 'var(--gold-a15)' : `${teamColor}12`;
          const itemAccentBd    = item.accent === 'gold' ? 'var(--gold-a30)' : `${teamColor}25`;
          const itemIdleColor   = item.accent === 'gold' ? 'var(--gold-a50)' : S.text2;
          return (
            <div key={item.to}>
              <NavLink to={item.to} end={item.end} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderRadius: 8, marginBottom: 1,
                textDecoration: 'none', minHeight: 44,
                background: isActive ? itemAccentBg : 'transparent',
                border: `1px solid ${isActive ? itemAccentBd : 'transparent'}`,
                color: isActive ? itemAccentColor : itemIdleColor,
                fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: isActive ? 600 : 400,
                contain: 'layout style',
              })}>
                <item.icon size={16} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {(item.badge || 0) > 0 && (
                  <span style={{ minWidth: 20, height: 18, padding: '0 5px', borderRadius: 9, background: teamColor, color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, fontFamily: 'var(--font-display)' }}>
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
                {hasChildren && (
                  <ChevronDown size={14} style={{
                    flexShrink: 0,
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.18s',
                    opacity: 0.6,
                  }} />
                )}
              </NavLink>
              {expanded && (
                <div style={{ marginLeft: 8, marginTop: 2, marginBottom: 4, borderLeft: `1.5px solid ${S.border}` }}>
                  {item.children.map(child => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      end={child.end}
                      style={({ isActive: childActive }) => ({
                        display: 'block',
                        padding: '8px 10px 8px 16px',
                        marginLeft: 8,
                        borderRadius: 6,
                        textDecoration: 'none',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: childActive ? 600 : 400,
                        color: childActive ? teamColor : S.text2,
                        background: childActive ? `${teamColor}10` : 'transparent',
                        minHeight: 36,
                      })}>
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ flexShrink: 0, padding: '8px 10px 12px', borderTop: `1px solid ${S.border}` }}>
        <NavLink to="/profile" style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, marginBottom: 2, textDecoration: 'none', minHeight: 44, background: isActive ? 'var(--ice-a8)' : 'transparent', color: isActive ? 'var(--ice)' : S.text2, fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500 })}>
          <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: 'var(--ice-a12)', border: '1px solid var(--ice-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12, color: 'var(--ice)' }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: S.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize: 9, color: 'var(--goal)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>● ONLINE</div>
          </div>
        </NavLink>
        {/* Apr 2026 — Activity link, conditional on transaction history.
            Same opt-in logic as Mobile/Desktop: only surfaces for users
            who've actually paid a spare/season fee (flag from /auth/me). */}
        {user?.hasPaymentActivity && (
          <NavLink to="/activity" style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, marginBottom: 2, textDecoration: 'none', minHeight: 44, background: isActive ? 'var(--ice-a8)' : 'transparent', color: isActive ? 'var(--ice)' : S.text2, fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500 })}>
            <Receipt size={15} /> Activity
          </NavLink>
        )}
        {user?.role === 'admin' && (
          <NavLink to="/admin" style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, textDecoration: 'none', minHeight: 44, color: isActive ? '#ff6040' : 'rgba(255,96,64,0.5)', fontFamily: 'var(--font-body)', fontSize: 14 })}>
            <Shield size={15} /> Admin
          </NavLink>
        )}
        <button onClick={onLogout} style={{ ...T, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, minHeight: 44, color: 'rgba(255,90,90,0.6)', fontFamily: 'var(--font-body)', fontSize: 14 }}>
          <LogOut size={15} /> Sign out
        </button>
        {/* Apr 30 2026 — version tag at sidebar bottom. Tappable for
            build details. Same component as Desktop/Mobile. */}
        <div style={{ paddingTop: 4, marginTop: 4, borderTop: `1px solid ${S.border}` }}>
          <VersionTag />
        </div>
      </div>
    </aside>
  );
}

// ─── Notification panel (right side on tablet) ────────────────────
function TabletNotifPanel({ notifications, onClose, onMarkRead, onMarkAllRead }) {
  // Apr 2026 — icons centralised; see utils/notifIcons.js.
  return (
    <>
      <div onClick={onClose} aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 490, background: 'rgba(0,0,0,0.4)' }} />
      <div role="dialog" aria-modal="true" aria-label="Notifications" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 340, zIndex: 500, background: S.surface, borderLeft: `1px solid ${S.border}`, display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)', contain: 'layout style paint', transform: 'translateZ(0)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${S.border}`, flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, letterSpacing: '0.06em', textTransform: 'uppercase', color: S.text }}>Notifications</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onMarkAllRead} style={{ ...T, fontSize: 12, color: 'var(--ice)', padding: '7px 12px', borderRadius: 6, background: 'var(--ice-a10)', border: '1px solid var(--ice-border)', minHeight: 44 }}>All read</button>
            <button onClick={onClose} aria-label="Close" style={{ ...T, padding: 8, borderRadius: 8, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} color={S.text2} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
          {notifications.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: S.text2, fontSize: 14 }}>
              <Bell size={32} style={{ margin: '0 auto 16px', opacity: 0.2, display: 'block' }} />
              All caught up!
            </div>
          ) : notifications.slice(0, 25).map(n => (
            <button key={n.id} onClick={() => onMarkRead(n.id)} style={{ ...T, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px', borderBottom: `1px solid ${S.border}`, background: n.read_at ? 'transparent' : 'var(--ice-a3)', minHeight: 64 }}>
              <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{iconForNotifType(n.type)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: S.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                  {!n.read_at && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ice)', flexShrink: 0 }} />}
                </div>
                {n.body && <p style={{ fontSize: 13, color: S.text2, margin: '4px 0 0', lineHeight: 1.45 }}>{n.body}</p>}
                <p style={{ fontSize: 11, color: S.text2, opacity: 0.5, marginTop: 5 }}>
                  {new Date(n.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── TabletLayout ─────────────────────────────────────────────────
export default function TabletLayout() {
  const { user, logout } = useAuth();
  const { offline }      = usePWAContext();
  const navigate         = useNavigate();
  const location         = useLocation();

  const [teams,         setTeams]         = useState([]);
  // May 1 2026 — see DesktopLayout for rationale.
  const [teamsLoaded,   setTeamsLoaded]   = useState(false);
  const [activeTeam,    setActiveTeam]    = useState(null);
  const { currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded } = useCurrentSeason(activeTeam?.id || null);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [showNotifs,    setShowNotifs]    = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Single call — result passed down as prop; TabletSidebar does NOT call this itself
  const sidebarWidth = useSidebarWidth();
  const teamColor    = activeTeam?.settings?.team_color || 'var(--ice)';

  // ── Visual Viewport API — Android tablets with Chrome have the same
  // dynamic toolbar issue as phones. Sets --mobile-h so the root height
  // always matches the true visible area even as the toolbar shows/hides.
  //
  // 2026-08-08 — same fix as MobileLayout (see the long comment there).
  // Writing vv.height unconditionally, on scroll as well as resize,
  // latched transient mid-animation heights into --mobile-h and left a
  // dead band below the app. Let CSS's 100dvh own the normal case and
  // measure only when the keyboard is genuinely up; drop the scroll
  // listener, which only ever changed the viewport OFFSET, not its size.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const isEditableFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true;
    };
    const update = () => {
      const delta = window.innerHeight - vv.height;
      const keyboardVisible = isEditableFocused() && delta > 150;
      document.documentElement.style.setProperty(
        '--mobile-h', keyboardVisible ? `${vv.height}px` : '100dvh'
      );
      document.documentElement.style.setProperty(
        '--keyboard-h', keyboardVisible ? `${delta}px` : '0px'
      );
      document.documentElement.classList.toggle('keyboard-open', keyboardVisible);
    };
    update();
    vv.addEventListener('resize', update, { passive: true });
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      vv.removeEventListener('resize', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      document.documentElement.classList.remove('keyboard-open');
    };
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--ice', teamColor);
  }, [teamColor]);

  const loadTeams = useCallback(async (lastTeamId = null, _attempt = 0) => {
    try {
      const { data } = await api.get('/teams');
      setTeams(data);
      // Apr 2026 — pickActiveTeam uses the saved last-team id we
      // pass in (avoids stale-closure bug — see DesktopLayout for
      // the full rationale).
      setActiveTeam(prev => pickActiveTeam(data, prev, lastTeamId));
      setTeamsLoaded(true);
    } catch (err) {
      // v2.5.184 — retry transient failures instead of flashing the
      // "not on a schedule" empty state. See DesktopLayout.
      console.error('Failed to load teams:', err);
      const status = err?.response?.status;
      if (status === 401) { setTeamsLoaded(true); return; }
      if (_attempt < 1) { setTimeout(() => loadTeams(lastTeamId, _attempt + 1), 1500); return; }
      setTeamsLoaded(true);
    }
  }, []);

  // Apr 2026 — persist active team to users.last_active_team_id.
  useEffect(() => {
    if (activeTeam?.id) saveLastTeamId(activeTeam.id);
  }, [activeTeam?.id]);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadTeams(user.lastActiveTeamId || null);
    }
    const token = localStorage.getItem('token');
    if (token) chatSocket.connect(token);
    return () => chatSocket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 2026-07-21 (multi-team email-link landing audit) — correct activeTeam
  // when it drifts from the team the CURRENT URL is actually pointing at.
  // See DesktopLayout.jsx for the full rationale — same fix, same shape,
  // applied to every layout since each keeps its own activeTeam state.
  useEffect(() => {
    if (!teamsLoaded || teams.length === 0) return;
    const match = location.pathname.match(/^\/team\/([^/]+)/);
    const urlTeamId = match ? match[1] : null;
    if (!urlTeamId || urlTeamId === activeTeam?.id) return;
    const urlTeam = teams.find(t => t.id === urlTeamId);
    if (!urlTeam) return; // not a member (or a bad id) — leave activeTeam as-is
    setActiveTeam(urlTeam);
    try { localStorage.setItem('gd:lastTeamId', urlTeamId); } catch { /* storage unavailable — non-fatal */ }
  }, [location.pathname, teams, teamsLoaded, activeTeam?.id]);

  useEffect(() => { loadNotifications(); }, []);

  useEffect(() => {
    const id = setInterval(loadNotifications, 45_000);
    return () => clearInterval(id);
  }, [loadNotifications]);

  useEffect(() => {
    const unsub = chatSocket.on('NEW_MESSAGE', () => {
      if (!location.pathname.includes('/chat')) setUnreadCount(c => c + 1);
    });
    return unsub;
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.includes('/chat')) setUnreadCount(0);
  }, [location.pathname]);

  useEffect(() => {
    const unsub = chatSocket.on('NEW_NOTIFICATION', notif => {
      if (notif) setNotifications(prev => [{ ...notif, read_at: null }, ...prev.filter(n => n.id !== notif.id)]);
      else loadNotifications();
    });
    return unsub;
  }, [loadNotifications]);

  const handleLogout = useCallback(() => { logout(); navigate('/login'); }, [logout, navigate]);
  const unreadNotifs = notifications.filter(n => !n.read_at).length;

  const markRead = async id => {
    // Capture nav target BEFORE the await — notifications state captured in this
    // closure may be stale if a WS event arrives during the API call
    const notif = notifications.find(n => n.id === id);
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(p => p.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      if (notif?.game_id) navigate(`/game/${notif.game_id}`);
      else if (notif?.team_id) navigate(`/team/${notif.team_id}/chat`);
      setShowNotifs(false);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(p => p.map(n => ({ ...n, read_at: new Date().toISOString() })));
    } catch {}
    setShowNotifs(false);
  };

  return (
    <div style={{ height: 'var(--mobile-h, 100dvh)', display: 'flex', flexDirection: 'column', background: S.bg, overflow: 'hidden' }}>

      {/* Offline strip */}
      {offline.isOffline && (
        <div style={{
          background: 'rgba(255,59,59,0.95)',
          padding: 'calc(env(safe-area-inset-top, 0px) + 8px) 20px 8px',
          textAlign: 'center', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff',
        }}>
          <WifiOff size={13} />
          <span>Offline — cached data</span>
        </div>
      )}

      {/* Top bar — uses dynamic sidebarWidth for correct offset on every orientation */}
      <div style={{
        background: S.topBar,
        borderBottom: `1px solid ${S.border}`,
        paddingTop: offline.isOffline ? 0 : 'env(safe-area-inset-top, 0px)',
        display: 'flex', alignItems: 'center',
        height: `calc(${offline.isOffline ? '0px' : 'env(safe-area-inset-top, 0px)'} + 52px)`,
        padding: `${offline.isOffline ? '0' : 'env(safe-area-inset-top, 0px)'} 20px 0`,
        paddingBottom: 0,
        flexShrink: 0,
        contain: 'layout style paint',
        transform: 'translateZ(0)',
        zIndex: 200,
      }}>
        {/* FIX: use sidebarWidth state (not the old SIDEBAR_W constant) so the
            title offset exactly matches the sidebar in every orientation.         */}
        <div style={{
          paddingLeft: sidebarWidth,
          height: 52, flex: 1,
          display: 'flex', alignItems: 'center',
        }}>
          {activeTeam && (
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600,
              color: S.text2, paddingLeft: 24,
            }}>
              {activeTeam.name}
              {activeTeam.league_name && (
                <span style={{ fontSize: 12, color: S.text2, opacity: 0.6, marginLeft: 8 }}>
                  · {activeTeam.league_name}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Notifications bell */}
        <div style={{ height: 52, display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => { setShowNotifs(p => !p); loadNotifications(); }}
            aria-label={`Notifications${unreadNotifs > 0 ? ` (${unreadNotifs} unread)` : ''}`}
            style={{
              ...T, padding: 10, borderRadius: 10, position: 'relative',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 48, minHeight: 48,
              background: showNotifs ? 'var(--ice-a8)' : 'transparent',
              border: `1px solid ${showNotifs ? 'var(--ice-a20)' : 'transparent'}`,
            }}
          >
            <Bell size={20} color={unreadNotifs > 0 ? teamColor : S.text2} />
            {unreadNotifs > 0 && (
              <span style={{ position: 'absolute', top: 8, right: 8, minWidth: 16, height: 16, padding: '0 3px', borderRadius: 8, background: '#ff3b3b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                {unreadNotifs > 9 ? '9+' : unreadNotifs}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* App store smart banner (tablet browsers only — hidden in Capacitor and standalone PWA) */}
      <AppBanner />

      {/* Split pane */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar — receives sidebarWidth as prop */}
        <TabletSidebar
          teams={teams}
          activeTeam={activeTeam}
          setActiveTeam={setActiveTeam}
          user={user}
          onLogout={handleLogout}
          unreadCount={unreadCount}
          sidebarWidth={sidebarWidth}
          currentSeasonId={currentSeasonId}
          currentSeasonName={currentSeasonName}
        />

        {/* Content pane */}
        <main style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'env(safe-area-inset-bottom, 20px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          contain: 'layout style',
        }}>
          <div style={{ padding: '20px 28px', maxWidth: 960, margin: '0 auto' }}>
            <Outlet context={{ teams, teamsLoaded, activeTeam, setActiveTeam, loadTeams, currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded }} />
          </div>
        </main>
      </div>

      {/* Notification panel */}
      {showNotifs && (
        <TabletNotifPanel
          notifications={notifications}
          onClose={() => setShowNotifs(false)}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
        />
      )}
      <OnboardingFlow teams={teams} activeTeam={activeTeam} />
      {/* PWA banners — update-available / push-nudge / offline. Mounted
          at layout level so tablet users see app-update notices too. */}
      <PWABanners />
    </div>
  );
}
